import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  linkWithPopup,
  sendPasswordResetEmail,
  updatePassword,
  User,
  isSignInWithEmailLink
} from '@angular/fire/auth';
import { environment } from '@pxl/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  constructor(
    private auth: Auth,
    @Inject(DOCUMENT) private document: Document,
    private http: HttpClient
  ) {}

  loginWithGoogle() {
    return signInWithPopup(this.auth, new GoogleAuthProvider());
  }

  get appUrl() {
    const location = this.document.location;
    const domain = location.port ? `${location.hostname}:${location.port}` : location.hostname;
    return `${location.protocol}//${domain}`;
  }

  get verifyUrl() {
    return `${this.appUrl}/auth/verify-email`;
  }

  async sendEmailVerification(email: string) {
    const encodedEmail = btoa(email);

    const actionCodeSettings = {
      url: `${this.verifyUrl}?code=${encodedEmail}`,
      handleCodeInApp: true
    };
    return sendSignInLinkToEmail(this.auth, email, actionCodeSettings);
  }

  async verifyEmail(code: string, password: string, signInWithGoogle: boolean) {
    const decodedEmail = atob(code);
    let { user } = await signInWithEmailLink(
      this.auth,
      decodedEmail,
      this.document.location.toString()
    );

    if (user.emailVerified) {
      if (password) {
        await updatePassword(user, password);
        return user;
      }
      if (signInWithGoogle) {
        ({ user } = await linkWithPopup(user, new GoogleAuthProvider()));
      }
    }

    return user;
  }

  async isEmailLinkValid() {
    return isSignInWithEmailLink(this.auth, this.document.location.toString());
  }

  async login(email: string, password: string) {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  async resetPassword(email: string) {
    return sendPasswordResetEmail(this.auth, email, { url: `${this.appUrl}/login` });
  }

  async register(email: string, password: string) {
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  async logout() {
    return signOut(this.auth);
  }

  fetchUsers() {
    const endpoint = `${environment.backendUrl}auth/users`;

    return this.http.get<{ users: User[] }>(endpoint);
  }

  setCustomClaims(uid: string, claims: Record<string, boolean | string | number>) {
    const endpoint = `${environment.backendUrl}auth/users/${uid}/set-custom-claims`;

    return this.http.post(endpoint, claims);
  }

  deleteUser(uid: string) {
    const endpoint = `${environment.backendUrl}auth/users/${uid}`;

    return this.http.delete(endpoint);
  }

  toggleDisableUser(uid: string) {
    const endpoint = `${environment.backendUrl}auth/users/${uid}/toggle-disabled`;

    return this.http.post(endpoint, {});
  }
}
